import { useRef, useState } from "react";
import { ArrowLeft, ArrowRight, RefreshCcw, Star, Upload, X } from "lucide-react";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { labelCls } from "./shared";

// Image manager for the product form. Edit mode (productId set): every action hits the API.
// New mode: files are staged locally with previews and uploaded by the parent after the product is created.
export default function ImageManager({ productId, images = [], staged = [], onServer, onStaged }) {
  const addRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [busy, setBusy] = useState(false);
  const isEdit = !!productId;

  const notifyServer = (imgs) => onServer && onServer(imgs);
  const notifyStaged = (files) => onStaged && onStaged(files);

  const reorderServer = async (urls) => {
    try {
      const { data } = await api.put(`/admin/products/${productId}/images`, { urls });
      notifyServer(data.images);
    } catch (err) { toast.error(apiError(err)); }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!isEdit) {
      const next = [...staged, ...files.map((f) => ({ file: f, preview: URL.createObjectURL(f) }))];
      notifyStaged(next);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/admin/products/${productId}/images`, fd);
      notifyServer(data.images);
      toast.success("Images added");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const removeAt = async (i) => {
    if (!isEdit) {
      const next = staged.filter((_, j) => j !== i);
      notifyStaged(next);
      return;
    }
    if (!window.confirm("Remove this image?")) return;
    try {
      const { data } = await api.delete(`/admin/products/${productId}/images`, { params: { url: images[i] } });
      notifyServer(data.images);
    } catch (err) { toast.error(apiError(err)); }
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (isEdit) {
      if (j < 0 || j >= images.length) return;
      const urls = [...images];
      [urls[i], urls[j]] = [urls[j], urls[i]];
      notifyServer(urls);
      reorderServer(urls);
    } else {
      if (j < 0 || j >= staged.length) return;
      const next = [...staged];
      [next[i], next[j]] = [next[j], next[i]];
      notifyStaged(next);
    }
  };

  const setMain = (i) => {
    if (i === 0) return;
    if (isEdit) {
      const urls = [images[i], ...images.filter((_, j) => j !== i)];
      notifyServer(urls);
      reorderServer(urls);
      toast.success("Main image updated");
    } else {
      const next = [staged[i], ...staged.filter((_, j) => j !== i)];
      notifyStaged(next);
    }
  };

  const doReplace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || replaceIdx == null || !isEdit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post(`/admin/products/${productId}/images`, fd);
      const newUrl = data.images[data.images.length - 1];
      const oldUrl = images[replaceIdx];
      const replaced = images.map((u, idx) => (idx === replaceIdx ? newUrl : u));
      await api.delete(`/admin/products/${productId}/images`, { params: { url: oldUrl } });
      const final = await api.put(`/admin/products/${productId}/images`, { urls: replaced });
      notifyServer(final.data.images);
      toast.success("Image replaced");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
    setReplaceIdx(null);
  };

  const list = isEdit ? images.map((u) => ({ url: u })) : staged.map((s) => ({ preview: s.preview }));

  return (
    <div data-testid="image-manager">
      <p className={labelCls}>Product images — first photo is the main image shown everywhere</p>
      <div className="mt-2 flex flex-wrap items-start gap-3">
        {list.map((item, i) => (
          <div key={item.url || item.preview} className="relative rounded-xl" data-testid={`image-item-${i}`}>
            <img src={item.url ? imgUrl(item.url) : item.preview} alt="" className={`h-24 w-20 rounded-xl object-cover ${i === 0 ? "ring-2 ring-primary" : ""}`} />
            {i === 0 && <span className="absolute -left-1 -top-1.5 rounded-full bg-primary px-1.5 py-0.5 text-[8px] tracking-widest text-white uppercase">Main</span>}
            <div className="mt-1 flex justify-center gap-0.5">
              <button type="button" data-testid={`image-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move left" className="rounded-full p-1 hover:bg-secondary disabled:opacity-30"><ArrowLeft size={12} /></button>
              <button type="button" data-testid={`image-right-${i}`} onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label="Move right" className="rounded-full p-1 hover:bg-secondary disabled:opacity-30"><ArrowRight size={12} /></button>
              <button type="button" data-testid={`image-main-${i}`} onClick={() => setMain(i)} aria-label="Set as main" className={`rounded-full p-1 hover:bg-secondary ${i === 0 ? "text-primary" : ""}`}><Star size={12} /></button>
              {isEdit && (
                <button type="button" data-testid={`image-replace-${i}`} onClick={() => { setReplaceIdx(i); replaceRef.current?.click(); }} aria-label="Replace" className="rounded-full p-1 hover:bg-secondary"><RefreshCcw size={12} /></button>
              )}
              <button type="button" data-testid={`image-remove-${i}`} onClick={() => removeAt(i)} aria-label="Remove" className="rounded-full p-1 text-destructive hover:bg-secondary"><X size={12} /></button>
            </div>
          </div>
        ))}
        <button type="button" data-testid="image-add-btn" onClick={() => addRef.current?.click()} disabled={busy}
          className="flex h-24 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-primary/50 text-[9px] uppercase tracking-widest text-primary hover:bg-secondary/50">
          <Upload size={16} /> {busy ? "…" : "Add"}
        </button>
        <input ref={addRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" multiple className="hidden" data-testid="image-add-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={replaceRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden" data-testid="image-replace-input" onChange={doReplace} />
      </div>
      <p className="mt-1.5 text-[11px] font-light text-muted-foreground">JPG, JPEG, PNG or WEBP · multiple selection supported · arrows reorder · star sets the main image.</p>
    </div>
  );
}
